import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  buildUrl, buildSizedUrl, COPY_SIZES,
  isResizable, fileKind, fileExt, formatSize, formatDateTime, usageText,
} from "../media";
import {
  IcoX, IcoChevronLeft, IcoChevronRight, IcoCopy, IcoCheck, IcoDownload,
  IcoExternal, IcoTrash, IcoGlobe,
} from "./Icons";

const PREVIEW_WIDTH = 1600;

// Xem lớn một file: ảnh/video ở bên trái, thông tin và các nút chép link theo
// cỡ ở bên phải. Mũi tên trái/phải để chuyển file, Esc để đóng.
export default function PreviewModal({
  items, index, path, usage,
  onNavigate, onClose, onCopy, onDelete, onDownload,
}) {
  const item = items[index];
  const [copiedKey, setCopiedKey] = useState(null);
  const [useOriginal, setUseOriginal] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const hasPrev = index > 0;
  const hasNext = index < items.length - 1;

  useEffect(() => {
    setUseOriginal(false);
    setLoaded(false);
    setCopiedKey(null);
  }, [item?.name]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && hasPrev) onNavigate(index - 1);
      if (e.key === "ArrowRight" && hasNext) onNavigate(index + 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, hasPrev, hasNext, onClose, onNavigate]);

  // Khóa cuộn trang nền khi đang xem lớn.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  if (!item) return null;

  const url = buildUrl(path, item.name);
  const kind = fileKind(item.name);
  const canResize = isResizable(item.name);
  const used = usage.files[item.name];
  const previewSrc = canResize && !useOriginal ? buildSizedUrl(path, item.name, PREVIEW_WIDTH) : url;

  const copy = (link, key, label) => {
    onCopy(link, url, label);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1800);
  };

  const rows = canResize
    ? [
        ...COPY_SIZES.map((sz) => ({
          key: sz.key,
          label: sz.label,
          hint: sz.hint,
          link: buildSizedUrl(path, item.name, sz.width),
          toast: `cỡ ${sz.label.toLowerCase()}`,
        })),
        { key: "o", label: "Gốc", hint: formatSize(item.size) || "nguyên kích thước", link: url, toast: "ảnh gốc" },
      ]
    : [{ key: "o", label: "Link file", hint: formatSize(item.size) || "", link: url, toast: "" }];

  return createPortal(
    <div className="preview" role="dialog" aria-modal="true" aria-label={`Xem ${item.name}`}>
      <div className="preview-stage" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        {kind === "video" ? (
          <video key={url} src={url} controls autoPlay playsInline />
        ) : (
          <img
            key={previewSrc}
            src={previewSrc}
            alt={item.name}
            className={loaded ? "is-loaded" : ""}
            onLoad={() => setLoaded(true)}
            onError={() => { if (!useOriginal && canResize) setUseOriginal(true); else setLoaded(true); }}
          />
        )}
        {kind !== "video" && !loaded && <span className="preview-spinner" aria-label="Đang tải ảnh" />}

        {hasPrev && (
          <button className="preview-nav prev" onClick={() => onNavigate(index - 1)} aria-label="File trước">
            <IcoChevronLeft size={22} />
          </button>
        )}
        {hasNext && (
          <button className="preview-nav next" onClick={() => onNavigate(index + 1)} aria-label="File sau">
            <IcoChevronRight size={22} />
          </button>
        )}
        <span className="preview-counter">{index + 1} / {items.length}</span>
      </div>

      <aside className="preview-panel">
        <div className="preview-panel-head">
          <span className={`type-chip static kind-${kind}`}>{fileExt(item.name)}</span>
          <button className="icon-btn ghost" onClick={onClose} aria-label="Đóng">
            <IcoX size={20} />
          </button>
        </div>

        <h3 className="preview-name" title={item.name}>{item.name}</h3>
        <dl className="preview-facts">
          {item.size > 0 && (<><dt>Dung lượng</dt><dd>{formatSize(item.size)}</dd></>)}
          {item.lastModified > 0 && (<><dt>Cập nhật</dt><dd>{formatDateTime(item.lastModified)}</dd></>)}
          <dt>Thư mục</dt><dd title={path || "Kho ảnh"}>{path || "Kho ảnh"}</dd>
        </dl>

        {used && (
          <div className="callout warn">
            <IcoGlobe size={16} />
            <span>{usageText(used)}</span>
          </div>
        )}

        <div className="preview-section">Sao chép link</div>
        <div className="link-rows">
          {rows.map((r) => (
            <button key={r.key} className={`link-row ${copiedKey === r.key ? "ok" : ""}`} onClick={() => copy(r.link, r.key, r.toast)}>
              <span className="link-row-text">
                <b>{r.label}</b>
                <span>{r.hint}</span>
              </span>
              {copiedKey === r.key ? <IcoCheck size={16} /> : <IcoCopy size={16} />}
            </button>
          ))}
        </div>

        <div className="preview-buttons">
          <a className="btn btn-quiet" href={url} target="_blank" rel="noreferrer">
            <IcoExternal size={16} /> <span>Mở file gốc</span>
          </a>
          <button className="btn btn-quiet" onClick={() => onDownload(item.name)}>
            <IcoDownload size={16} /> <span>Tải về</span>
          </button>
        </div>

        {onDelete && (
          <button className="btn btn-danger-quiet preview-delete" onClick={() => onDelete(item.name)}>
            <IcoTrash size={16} /> <span>Chuyển vào thùng rác</span>
          </button>
        )}
      </aside>
    </div>,
    document.body
  );
}
